/**
 * AorraToast — Custom notification system matching Figma design.
 *
 * Usage (replaces Sonner toast):
 *   import { aorraToast } from "./components/AorraToast";
 *   aorraToast.success("Guardado exitosamente");
 *   aorraToast.error("Error al guardar");
 *
 * Also monkey-patches `toast` from sonner so existing code works transparently.
 */
import { useState, useEffect, useCallback, useRef, createContext } from "react";
import * as Dialog from "@radix-ui/react-dialog";

/* ─── Context ─── */
const ToastContext = createContext(null);

let _showToast = () => {};

/* ─── Public API (mirrors toast.success / toast.error) ─── */
export const aorraToast = {
  success: (message, opts = {}) => _showToast({ type: "success", message, ...opts }),
  error:   (message, opts = {}) => _showToast({ type: "error",   message, ...opts }),
  info:    (message, opts = {}) => _showToast({ type: "success", message, ...opts }),
  warning: (message, opts = {}) => _showToast({ type: "error",   message, ...opts }),
};

/* ─── Provider ─── */
export function AorraToastProvider({ children }) {
  const [toastData, setToastData] = useState(null);
  const [visible, setVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const closeTimerRef = useRef(null);

  const show = useCallback((data) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    setAnimatingOut(false);
    setToastData(data);
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    // Closing feedback must always win over a previous animation or timer.
    // Otherwise an error modal can become impossible to dismiss after a quick
    // retry/close sequence.
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    setAnimatingOut(false);
    setVisible(false);
    setToastData(null);
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  // Register global function
  useEffect(() => {
    _showToast = show;
    return () => { _showToast = () => {}; };
  }, [show]);

  // Auto-close success after 3 seconds
  useEffect(() => {
    if (visible && toastData?.type === "success") {
      const timer = setTimeout(close, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, toastData, close]);

  return (
    <ToastContext.Provider value={{ show, close }}>
      {children}
      {visible && toastData && (
        <AorraToastModal data={toastData} onClose={close} animatingOut={animatingOut} />
      )}
    </ToastContext.Provider>
  );
}

/* ─── Modal Component ─── */
function AorraToastModal({ data, onClose, animatingOut }) {
  const previousFocusRef = useRef(document.activeElement);
  const isError = data.type === "error";
  const iconSrc = isError ? "/Iconos/Alertas/error.png" : "/Iconos/Alertas/exitoso.png";

  // Determine title
  const title = data.title || (isError ? "¡Algo salió mal!" : "¡Listo!");

  // Determine body text — message is the main content, description is supplementary
  const body = data.description || data.message || "";

  // Button labels
  const primaryLabel = data.primaryLabel || (data.onRetry ? "Reintentar" : "Aceptar");
  const secondaryLabel = data.secondaryLabel || "Cerrar";

  // Callbacks
  const onPrimary = data.onRetry || data.onPrimary || onClose;
  const showSecondary = isError;
  const dismiss = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    onClose();
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
      {/* Backdrop */}
      <Dialog.Overlay
        onClick={dismiss}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 99998,
          animation: animatingOut ? "aorraFadeOut 0.25s ease forwards" : "aorraFadeIn 0.25s ease forwards",
        }}
      />

      {/* Modal */}
      <Dialog.Content
        {...(!body ? { "aria-describedby": undefined } : {})}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
        }}
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "fixed",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "40px 30px 32px",
          gap: 32,
          width: 420,
          maxWidth: "calc(100vw - 40px)",
          background: "#FFFFFF",
          boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
          borderRadius: 20,
          animation: animatingOut
            ? "aorraSlideOut 0.25s ease forwards"
            : "aorraSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
        }}
      >
        <button
          type="button"
          aria-label="Cerrar alerta"
          onClick={dismiss}
          style={{ position: "absolute", top: 14, right: 16, width: 36, height: 36, border: "none", background: "transparent", color: "#396BE9", cursor: "pointer", fontSize: 30, lineHeight: "24px", zIndex: 1 }}
        >×</button>
        {/* Content Stack */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
          {/* Icon */}
          <img
            src={iconSrc}
            alt={isError ? "Error" : "Éxito"}
            style={{ width: 112, height: 112, objectFit: "contain" }}
          />

          {/* Text group */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, maxWidth: 300 }}>
            {/* Title */}
            <Dialog.Title style={{
              margin: 0,
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 24,
              lineHeight: "33px",
              textAlign: "center",
              color: "#000000",
            }}>
              {title}
            </Dialog.Title>

            {/* Body */}
            {body && (
              <Dialog.Description style={{
                margin: 0,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 400,
                fontSize: 16,
                lineHeight: "150%",
                textAlign: "center",
                color: "#5C5C5C",
              }}>
                {body}
              </Dialog.Description>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
          {/* Primary Button */}
          <button
            type="button"
            onClick={() => { onClose(); if (onPrimary !== onClose) onPrimary(); }}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px",
              width: "100%",
              height: 46,
              background: "#396BE9",
              borderRadius: 20,
              border: "none",
              cursor: "pointer",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "22px",
              color: "#FFFFFF",
              transition: "opacity 0.15s ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
          >
            {primaryLabel}
          </button>

          {/* Secondary Button (errors only) */}
          {showSecondary && (
            <button
              type="button"
              onClick={dismiss}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px",
                width: "100%",
                height: 46,
                background: "#E1E9FC",
                borderRadius: 20,
                border: "none",
                cursor: "pointer",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "22px",
                color: "#396BE9",
                transition: "opacity 0.15s ease",
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </Dialog.Content>

      {/* Keyframe animations (injected once) */}
      <style>{`
        @keyframes aorraFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes aorraFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes aorraSlideIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes aorraSlideOut {
          from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          to { opacity: 0; transform: translate(-50%, -50%) scale(0.85); }
        }
      `}</style>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default AorraToastModal;
